import React, { useState, useEffect } from "react";
import {
  Tag,
  Bot,
  Share2,
  MessageCircle,
  Braces,
  BrainCircuit,
  Map as MapIcon,
  Globe,
  Pencil,
  Sparkles,
  Save,
  Plus,
  CheckCircle2,
  XCircle,
  Search,
  FileStack,
  ToggleRight,
  Link2,
  X,
  Check,
  ChevronRight,
  Eye,
  Image as ImageIcon,
} from "lucide-react";
import "../css/Seoadmin.css";

const API = "/api/seo";

const DEFAULT_FORM = {
  pageSlug: "",
  meta:      { title: "", description: "", keywords: "", canonical: "" },
  robots:    { index: true, follow: true },
  openGraph: { title: "", description: "", image: "", url: "", type: "website" },
  twitter:   { card: "summary_large_image", title: "", description: "", image: "" },
  jsonLd:    "",
  llmTxt: "", 
  sitemap: { include: true, changefreq: "weekly", priority: "0.8", lastmod: "" }
};

// icon + accent color per section — decorative only, all primary actions
// (save button, focus rings, mode badge) still use the brand navy/blue.
const SECTIONS = [
  { key: "meta",      icon: Tag,          accent: "meta",      label: "Meta Tags",       desc: "Title, description & keywords" },
  { key: "robots",    icon: Bot,          accent: "robots",    label: "Robots Settings", desc: "Indexing & crawl directives"   },
  { key: "openGraph", icon: Share2,       accent: "openGraph", label: "Open Graph",      desc: "Facebook & social sharing"     },
  { key: "twitter",   icon: MessageCircle,accent: "twitter",   label: "Twitter Card",    desc: "Twitter preview data"          },
  { key: "jsonLd",    icon: Braces,       accent: "jsonLd",    label: "Structured Data", desc: "JSON-LD schema markup"         },
  { key: "llmTxt",    icon: BrainCircuit, accent: "llmTxt",    label: "LLM.txt (AEO)",   desc: "AI-readable structured content" },
  { key: "sitemap",   icon: MapIcon,      accent: "sitemap",   label: "Sitemap",         desc: "XML sitemap configuration" },
];

export default function SEOSchema() {
  const [form, setForm]         = useState(DEFAULT_FORM);
  const [slugList, setSlugList] = useState([]);          // existing page slugs from DB
  const [mode, setMode]         = useState("new");       // "new" | "edit"
  const [activeSection, setActiveSection] = useState("meta"); // which panel is showing
  const [notif, setNotif]       = useState(null);
  const [loading, setLoading]   = useState(false);

  // ── fetch all existing slugs on mount ──
  useEffect(() => {
    fetch(API)
      .then((r) => r.json())
      .then((data) => {
        // expects either an array of docs or { data: [] }
        const docs = Array.isArray(data) ? data : data.data ?? [];
        setSlugList(docs.map((d) => d.pageSlug));
      })
      .catch(() => {}); // silently fail if API not running
  }, []);

  // ── load existing record when a slug is chosen ──
  const loadSlug = async (slug) => {
    if (!slug) { setForm(DEFAULT_FORM); setMode("new"); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/${slug}`);
      const data = await res.json();
      const doc  = data.data ?? data;
      setForm({
        pageSlug:  doc.pageSlug ?? slug,
        meta:      { title: doc.meta?.title ?? "", description: doc.meta?.description ?? "", keywords: Array.isArray(doc.meta?.keywords) ? doc.meta.keywords.join(", ") : (doc.meta?.keywords ?? ""), canonical: doc.meta?.canonical ?? "" },
        robots:    { index: doc.robots?.index ?? true, follow: doc.robots?.follow ?? true },
        openGraph: { title: doc.openGraph?.title ?? "", description: doc.openGraph?.description ?? "", image: doc.openGraph?.image ?? "", url: doc.openGraph?.url ?? "", type: doc.openGraph?.type ?? "website" },
        twitter:   { card: doc.twitter?.card ?? "summary_large_image", title: doc.twitter?.title ?? "", description: doc.twitter?.description ?? "", image: doc.twitter?.image ?? "" },
        jsonLd:    doc.jsonLd ? JSON.stringify(doc.jsonLd, null, 2) : "",
        llmTxt:    doc.llmTxt ?? "",
       sitemap: {
  include: doc.sitemap?.include ?? true,
  changefreq: doc.sitemap?.changefreq ?? "weekly",
  priority: String(doc.sitemap?.priority ?? "0.8"),
  lastmod: doc.sitemap?.lastmod ?? "",
},
      });
      setMode("edit");
    } catch {
      notify("Failed to load page data", "error");
    } finally {
      setLoading(false);
    }
  };

  const notify = (msg, type = "success") => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3500);
  };

  const handleChange = (section, field, value) => {
    setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.pageSlug.trim()) { notify("Page slug is required", "error"); return; }

    let parsedJsonLd = {};
    try {
      parsedJsonLd = form.jsonLd.trim() ? JSON.parse(form.jsonLd) : {};
    } catch {
      notify("Invalid JSON-LD — please fix before saving", "error");
      return;
    }

    // shape matches mongoose schema exactly
    const payload = {
      pageSlug:  form.pageSlug.trim(),
      meta: {
        title:       form.meta.title,
        description: form.meta.description,
        keywords:    form.meta.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        canonical:   form.meta.canonical,
      },
      robots: {
        index:  form.robots.index,
        follow: form.robots.follow,
      },
      openGraph: {
        title:       form.openGraph.title,
        description: form.openGraph.description,
        image:       form.openGraph.image,
        url:         form.openGraph.url,
        type:        form.openGraph.type,
      },
      twitter: {
        card:        form.twitter.card,
        title:       form.twitter.title,
        description: form.twitter.description,
        image:       form.twitter.image,
      },
      jsonLd: parsedJsonLd,
      llmTxt: form.llmTxt,
      sitemap: {
  include:    form.sitemap.include,
  changefreq: form.sitemap.changefreq,
  priority:   parseFloat(form.sitemap.priority),
  lastmod:    form.sitemap.lastmod || null,
},
    };

    setLoading(true);
    try {
      const method = mode === "edit" ? "PUT" : "POST";
      const url    = mode === "edit" ? `${API}/${form.pageSlug}` : API;
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) {
  throw new Error("Failed");
}
      notify(mode === "edit" ? "SEO record updated!" : "New SEO record created!");
      if (mode === "new" && !slugList.includes(form.pageSlug)) {
        setSlugList((s) => [...s, form.pageSlug]);
      }
      setMode("edit");
    } catch {
      notify("Failed to save — check your connection", "error");
    } finally {
      setLoading(false);
    }
  };

  const jsonLdValid = form.jsonLd.trim()
    ? (() => { try { JSON.parse(form.jsonLd); return true; } catch { return false; } })()
    : null;

  const activeMeta = SECTIONS.find((s) => s.key === activeSection);
  const displayUrl = form.meta.canonical || `yoursite.com/${form.pageSlug || "…"}`;

  return (
    <>
      <div className="seo-wrap">

        {/* ── Top Bar ── */}
        <div className="seo-topbar">
          <div className="seo-topbar-left">
            <div className="seo-page-icon">
              <Search size={20} />
            </div>
            <div>
              <h1>SEO Management</h1>
              <p>View and manage meta, Open Graph, Twitter &amp; structured data</p>
            </div>
          </div>

          <div className="seo-topbar-right">
            <div className="seo-mini-stats">
              <span className="mini-stat">
                <FileStack size={13} />
                {slugList.length} pages
              </span>
              <span className={`mini-stat ${form.robots.index ? "on" : "off"}`}>
                <ToggleRight size={13} />
                Index {form.robots.index ? "on" : "off"}
              </span>
              <span className={`mini-stat ${form.robots.follow ? "on" : "off"}`}>
                <Link2 size={13} />
                Follow {form.robots.follow ? "on" : "off"}
              </span>
            </div>
            <div className="seo-mode-badge" data-mode={mode}>
              {mode === "edit" ? <Pencil size={13} /> : <Sparkles size={13} />}
              {mode === "edit" ? "Editing" : "New Record"}
            </div>
          </div>
        </div>

        <form className="seo-form" onSubmit={handleSubmit}>

          {/* ── Page Slug bar ── */}
          <div className="seo-card slug-card">
            <div className="slug-card-icon"><Globe size={18} /></div>
            <div className="slug-card-body">
              <label className="slug-card-label">Page Slug <span className="required">*</span></label>

              <div className="slug-controls">
                <select
                  className="slug-select"
                  value={mode === "edit" ? form.pageSlug : ""}
                  onChange={(e) => loadSlug(e.target.value)}
                >
                  <option value="">— create new page —</option>
                  {slugList.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>

                {mode === "new" && (
                  <div className="slug-row">
                    <span className="slug-prefix">yoursite.com/</span>
                    <input
                      type="text"
                      value={form.pageSlug}
                      placeholder="e.g. home, about, products"
                      onChange={(e) => setForm((f) => ({ ...f, pageSlug: e.target.value.replace(/\s+/g, "-") }))}
                    />
                  </div>
                )}

                {mode === "edit" && (
                  <div className="current-slug-display">
                    <span className="slug-prefix">yoursite.com/</span>
                    <strong>{form.pageSlug}</strong>
                    <button type="button" className="new-page-btn" onClick={() => { setForm(DEFAULT_FORM); setMode("new"); setActiveSection("meta"); }}>
                      <Plus size={13} />
                      New Page
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Rail + Panel layout ── */}
          <div className="seo-layout">

            {/* left icon rail */}
            <nav className="seo-nav" aria-label="SEO sections">
              {SECTIONS.map(({ key, icon: Icon, accent, label }) => (
                <button
                  type="button"
                  key={key}
                  className={`seo-nav-item ${activeSection === key ? "active" : ""}`}
                  onClick={() => setActiveSection(key)}
                >
                  <span className={`section-icon icon-${accent}`}><Icon size={17} /></span>
                  <span className="seo-nav-label">{label}</span>
                  {key === "jsonLd" && jsonLdValid === false && <span className="nav-dot warn" />}
                  <ChevronRight size={14} className="seo-nav-chevron" />
                </button>
              ))}
            </nav>

            {/* right content panel */}
            <div className="seo-panel seo-card">
              <div className="seo-panel-header">
                <span className={`section-icon icon-${activeMeta.accent}`}><activeMeta.icon size={18} /></span>
                <div>
                  <div className="section-label">{activeMeta.label}</div>
                  <div className="section-desc">{activeMeta.desc}</div>
                </div>
              </div>

              <div className="seo-panel-body">

                {/* ── meta ── */}
                {activeSection === "meta" && (
                  <>
                    <div className="field-row cols-2">
                      <div className="field">
                        <label>Title</label>
                        <input type="text" placeholder="Page title (50–60 chars)" value={form.meta.title}
                          onChange={(e) => handleChange("meta", "title", e.target.value)} />
                        <span className="field-hint">{form.meta.title.length} / 60 chars</span>
                      </div>
                      <div className="field">
                        <label>Canonical URL</label>
                        <input type="text" placeholder="https://yoursite.com/page" value={form.meta.canonical}
                          onChange={(e) => handleChange("meta", "canonical", e.target.value)} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Meta Description</label>
                      <textarea placeholder="Compelling description (150–160 chars)" value={form.meta.description}
                        onChange={(e) => handleChange("meta", "description", e.target.value)} />
                      <span className="field-hint">{form.meta.description.length} / 160 chars</span>
                    </div>
                    <div className="field">
                      <label>Keywords <span className="field-tag">Array — comma separated</span></label>
                      <input type="text" placeholder="seo, metadata, schema" value={form.meta.keywords}
                        onChange={(e) => handleChange("meta", "keywords", e.target.value)} />
                      {form.meta.keywords && (
                        <div className="keyword-chips">
                          {form.meta.keywords.split(",").map((k) => k.trim()).filter(Boolean).map((k, i) => (
                            <span key={i} className="chip">{k}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* signature element: live search-result preview */}
                    <div className="preview-block">
                      <div className="preview-label"><Eye size={12} /> Search result preview</div>
                      <div className="serp-preview">
                        <div className="serp-url">{displayUrl}</div>
                        <div className="serp-title">{form.meta.title || "Page title will appear here"}</div>
                        <div className="serp-desc">{form.meta.description || "Meta description will appear here, summarizing the page for search results."}</div>
                      </div>
                    </div>
                  </>
                )}

                {/* ── robots ── */}
                {activeSection === "robots" && (
                  <div className="robots-row">
                    {[
                      { rkey: "index",  label: "Index",       desc: "Allow search engines to index this page" },
                      { rkey: "follow", label: "Follow Links", desc: "Allow crawlers to follow links on this page" },
                    ].map(({ rkey, label: rl, desc: rd }) => (
                      <label className={`toggle-label ${form.robots[rkey] ? "active" : ""}`} key={rkey}>
                        <div className="toggle-wrap">
                          <input type="checkbox" checked={form.robots[rkey]}
                            onChange={(e) => handleChange("robots", rkey, e.target.checked)} />
                          <span className="toggle-track" />
                        </div>
                        <div className="toggle-text">
                          <span className="toggle-name">{rl}</span>
                          <span className="toggle-desc">{rd}</span>
                        </div>
                        <span className={`robot-status ${form.robots[rkey] ? "on" : "off"}`}>
                          {form.robots[rkey] ? "ON" : "OFF"}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {/* ── openGraph ── */}
                {activeSection === "openGraph" && (
                  <>
                    <div className="field-row cols-2">
                      <div className="field">
                        <label>OG Title</label>
                        <input type="text" placeholder="Social share title" value={form.openGraph.title}
                          onChange={(e) => handleChange("openGraph", "title", e.target.value)} />
                      </div>
                      <div className="field">
                        <label>OG Type</label>
                        <select value={form.openGraph.type} onChange={(e) => handleChange("openGraph", "type", e.target.value)}>
                          <option value="website">website</option>
                          <option value="article">article</option>
                          <option value="product">product</option>
                          <option value="profile">profile</option>
                        </select>
                      </div>
                    </div>
                    <div className="field">
                      <label>OG Description</label>
                      <textarea placeholder="Description shown when sharing on Facebook / LinkedIn" value={form.openGraph.description}
                        onChange={(e) => handleChange("openGraph", "description", e.target.value)} />
                    </div>
                    <div className="field-row cols-2">
                      <div className="field">
                        <label>OG Image URL</label>
                        <input type="text" placeholder="https://yoursite.com/og-image.jpg" value={form.openGraph.image}
                          onChange={(e) => handleChange("openGraph", "image", e.target.value)} />
                      </div>
                      <div className="field">
                        <label>OG URL</label>
                        <input type="text" placeholder="https://yoursite.com" value={form.openGraph.url}
                          onChange={(e) => handleChange("openGraph", "url", e.target.value)} />
                      </div>
                    </div>

                    {/* signature element: live social card preview */}
                    <div className="preview-block">
                      <div className="preview-label"><Eye size={12} /> Share card preview</div>
                      <div className="social-preview">
                        <div className="social-preview-image">
                          {form.openGraph.image
                            ? <img src={form.openGraph.image} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            : <ImageIcon size={22} />}
                        </div>
                        <div className="social-preview-body">
                          <div className="social-preview-domain">{(form.openGraph.url || "yoursite.com").replace(/^https?:\/\//, "")}</div>
                          <div className="social-preview-title">{form.openGraph.title || "Open Graph title"}</div>
                          <div className="social-preview-desc">{form.openGraph.description || "Open Graph description"}</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ── twitter ── */}
                {activeSection === "twitter" && (
                  <>
                    <div className="field-row cols-2">
                      <div className="field">
                        <label>Twitter Title</label>
                        <input type="text" placeholder="Card title" value={form.twitter.title}
                          onChange={(e) => handleChange("twitter", "title", e.target.value)} />
                      </div>
                      <div className="field">
                        <label>Card Type</label>
                        <select value={form.twitter.card} onChange={(e) => handleChange("twitter", "card", e.target.value)}>
                          <option value="summary_large_image">summary_large_image</option>
                          <option value="summary">summary</option>
                          <option value="app">app</option>
                          <option value="player">player</option>
                        </select>
                      </div>
                    </div>
                    <div className="field">
                      <label>Twitter Description</label>
                      <textarea placeholder="Card description shown on Twitter" value={form.twitter.description}
                        onChange={(e) => handleChange("twitter", "description", e.target.value)} />
                    </div>
                    <div className="field">
                      <label>Twitter Image URL</label>
                      <input type="text" placeholder="https://yoursite.com/twitter-card.jpg" value={form.twitter.image}
                        onChange={(e) => handleChange("twitter", "image", e.target.value)} />
                    </div>

                    <div className="preview-block">
                      <div className="preview-label"><Eye size={12} /> Card preview</div>
                      <div className="social-preview">
                        <div className="social-preview-image">
                          {form.twitter.image
                            ? <img src={form.twitter.image} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            : <ImageIcon size={22} />}
                        </div>
                        <div className="social-preview-body">
                          <div className="social-preview-domain">{form.twitter.card}</div>
                          <div className="social-preview-title">{form.twitter.title || "Twitter card title"}</div>
                          <div className="social-preview-desc">{form.twitter.description || "Twitter card description"}</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ── jsonLd ── */}
                {activeSection === "jsonLd" && (
                  <div className="field">
                    <label>JSON-LD Schema <span className="field-tag">Mixed type — free JSON</span></label>
                    <textarea
                      className="json-editor tall"
                      placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "WebPage",\n  "name": "Home"\n}'}
                      value={form.jsonLd}
                      onChange={(e) => setForm((f) => ({ ...f, jsonLd: e.target.value }))}
                    />
                    <span className={`field-hint json-status ${jsonLdValid === false ? "invalid" : jsonLdValid === true ? "valid" : ""}`}>
                      {jsonLdValid === true && (<><CheckCircle2 size={13} /> Valid JSON</>)}
                      {jsonLdValid === false && (<><XCircle size={13} /> Invalid JSON</>)}
                      {jsonLdValid === null && "Paste valid JSON-LD structured data markup"}
                    </span>
                  </div>
                )}

                {activeSection === "llmTxt" && (
                  <div className="field">
                    <label>
                      LLM.txt Content
                      <span className="field-tag">AI / AEO optimized plain text</span>
                    </label>

                    <textarea
                      className="json-editor tall"
                      placeholder={`# About Our Vehicles

We manufacture electric vehicles under the following categories:
- N-First
- Diplos
- Institutional

Our mission is...`}
                      value={form.llmTxt}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, llmTxt: e.target.value }))
                      }
                    />

                    <span className="field-hint">
                      This content will be served as plain text for AI systems.
                    </span>
                  </div>
                )}

                {activeSection === "sitemap" && (
                  <>
                    <div className="robots-row">
                      <label className={`toggle-label ${form.sitemap.include ? "active" : ""}`}>
                        <div className="toggle-wrap">
                          <input type="checkbox" checked={form.sitemap.include}
                            onChange={(e) => handleChange("sitemap", "include", e.target.checked)} />
                          <span className="toggle-track" />
                        </div>
                        <div className="toggle-text">
                          <span className="toggle-name">Include in Sitemap</span>
                          <span className="toggle-desc">Add this page to the XML sitemap</span>
                        </div>
                        <span className={`robot-status ${form.sitemap.include ? "on" : "off"}`}>
                          {form.sitemap.include ? "ON" : "OFF"}
                        </span>
                      </label>
                    </div>

                    <div className="field-row cols-2">
                      <div className="field">
                        <label>Change Frequency</label>
                        <select value={form.sitemap.changefreq}
                          onChange={(e) => handleChange("sitemap", "changefreq", e.target.value)}>
                          <option value="always">always</option>
                          <option value="hourly">hourly</option>
                          <option value="daily">daily</option>
                          <option value="weekly">weekly</option>
                          <option value="monthly">monthly</option>
                          <option value="yearly">yearly</option>
                          <option value="never">never</option>
                        </select>
                        <span className="field-hint">How often the page content changes</span>
                      </div>
                      <div className="field">
                        <label>Priority <span className="field-tag">0.0 – 1.0</span></label>
                        <select value={form.sitemap.priority}
                          onChange={(e) => handleChange("sitemap", "priority", e.target.value)}>
                          {["1.0","0.9","0.8","0.7","0.6","0.5","0.4","0.3","0.2","0.1","0.0"].map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                        <span className="field-hint">Relative importance vs other pages</span>
                      </div>
                    </div>

                    <div className="field">
                      <label>Last Modified Date <span className="field-tag">Optional</span></label>
                      <input type="date" value={form.sitemap.lastmod}
                        onChange={(e) => handleChange("sitemap", "lastmod", e.target.value)} />
                      <span className="field-hint">Leave blank to use today's date automatically</span>
                    </div>
                  </>
                )}

              </div>
            </div>
          </div>

          {/* ── Submit bar (always accessible, independent of active section) ── */}
          <div className="submit-row">
            <span className="save-hint">
              {mode === "edit" ? `Updating: /${form.pageSlug}` : "Creating new page SEO record"}
            </span>
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? "Saving..." : (
                <>
                  <Save size={15} />
                  {mode === "edit" ? "Update SEO" : "Save SEO"}
                </>
              )}
            </button>
          </div>

        </form>
      </div>

      {/* ── Toast ── */}
      {notif && (
        <div className={`notif ${notif.type === "error" ? "error" : ""}`}>
          <span className="notif-icon">{notif.type === "error" ? <X size={14} /> : <Check size={14} />}</span>
          {notif.msg}
        </div>
      )}
    </>
  );
}

// import React, { useState, useEffect } from "react";
// import {
//   Tag,
//   Bot,
//   Share2,
//   MessageCircle,
//   Braces,
//   BrainCircuit,
//   Map as MapIcon,
//   Globe,
//   Pencil,
//   Sparkles,
//   ChevronDown,
//   Save,
//   Plus,
//   CheckCircle2,
//   XCircle,
//   Search,
//   FileStack,
//   ToggleRight,
//   Link2,
//   X,
//   Check,
// } from "lucide-react";
// import "../css/SEO.css";

// const API = "/api/seo";

// const DEFAULT_FORM = {
//   pageSlug: "",
//   meta:      { title: "", description: "", keywords: "", canonical: "" },
//   robots:    { index: true, follow: true },
//   openGraph: { title: "", description: "", image: "", url: "", type: "website" },
//   twitter:   { card: "summary_large_image", title: "", description: "", image: "" },
//   jsonLd:    "",
//   llmTxt: "", 
//   sitemap: { include: true, changefreq: "weekly", priority: "0.8", lastmod: "" }
// };

// // icon + accent color per section — decorative only, all primary actions
// // (save button, focus rings, mode badge) still use the brand navy/blue.
// const SECTIONS = [
//   { key: "meta",      icon: Tag,          accent: "meta",      label: "Meta Tags",       desc: "Title, description & keywords" },
//   { key: "robots",    icon: Bot,          accent: "robots",    label: "Robots Settings", desc: "Indexing & crawl directives"   },
//   { key: "openGraph", icon: Share2,       accent: "openGraph", label: "Open Graph",      desc: "Facebook & social sharing"     },
//   { key: "twitter",   icon: MessageCircle,accent: "twitter",   label: "Twitter Card",    desc: "Twitter preview data"          },
//   { key: "jsonLd",    icon: Braces,       accent: "jsonLd",    label: "Structured Data", desc: "JSON-LD schema markup"         },
//   { key: "llmTxt",    icon: BrainCircuit, accent: "llmTxt",    label: "LLM.txt (AEO)",   desc: "AI-readable structured content" },
//   { key: "sitemap",   icon: MapIcon,      accent: "sitemap",   label: "Sitemap",         desc: "XML sitemap configuration" },
// ];

// export default function SEOSchema() {
//   const [form, setForm]         = useState(DEFAULT_FORM);
//   const [slugList, setSlugList] = useState([]);          // existing page slugs from DB
//   const [mode, setMode]         = useState("new");       // "new" | "edit"
//   const [open, setOpen]         = useState({ meta: true, robots: false, openGraph: false, twitter: false, jsonLd: false, llmTxt: false, sitemap: false });
//   const [notif, setNotif]       = useState(null);
//   const [loading, setLoading]   = useState(false);

//   // ── fetch all existing slugs on mount ──
//   useEffect(() => {
//     fetch(API)
//       .then((r) => r.json())
//       .then((data) => {
//         // expects either an array of docs or { data: [] }
//         const docs = Array.isArray(data) ? data : data.data ?? [];
//         setSlugList(docs.map((d) => d.pageSlug));
//       })
//       .catch(() => {}); // silently fail if API not running
//   }, []);

//   // ── load existing record when a slug is chosen ──
//   const loadSlug = async (slug) => {
//     if (!slug) { setForm(DEFAULT_FORM); setMode("new"); return; }
//     setLoading(true);
//     try {
//       const res  = await fetch(`${API}/${slug}`);
//       const data = await res.json();
//       const doc  = data.data ?? data;
//       setForm({
//         pageSlug:  doc.pageSlug ?? slug,
//         meta:      { title: doc.meta?.title ?? "", description: doc.meta?.description ?? "", keywords: Array.isArray(doc.meta?.keywords) ? doc.meta.keywords.join(", ") : (doc.meta?.keywords ?? ""), canonical: doc.meta?.canonical ?? "" },
//         robots:    { index: doc.robots?.index ?? true, follow: doc.robots?.follow ?? true },
//         openGraph: { title: doc.openGraph?.title ?? "", description: doc.openGraph?.description ?? "", image: doc.openGraph?.image ?? "", url: doc.openGraph?.url ?? "", type: doc.openGraph?.type ?? "website" },
//         twitter:   { card: doc.twitter?.card ?? "summary_large_image", title: doc.twitter?.title ?? "", description: doc.twitter?.description ?? "", image: doc.twitter?.image ?? "" },
//         jsonLd:    doc.jsonLd ? JSON.stringify(doc.jsonLd, null, 2) : "",
//         llmTxt:    doc.llmTxt ?? "",
//        sitemap: {
//   include: doc.sitemap?.include ?? true,
//   changefreq: doc.sitemap?.changefreq ?? "weekly",
//   priority: String(doc.sitemap?.priority ?? "0.8"),
//   lastmod: doc.sitemap?.lastmod ?? "",
// },
//       });
//       setMode("edit");
//     } catch {
//       notify("Failed to load page data", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const notify = (msg, type = "success") => {
//     setNotif({ msg, type });
//     setTimeout(() => setNotif(null), 3500);
//   };

//   const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

//   const handleChange = (section, field, value) => {
//     setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!form.pageSlug.trim()) { notify("Page slug is required", "error"); return; }

//     let parsedJsonLd = {};
//     try {
//       parsedJsonLd = form.jsonLd.trim() ? JSON.parse(form.jsonLd) : {};
//     } catch {
//       notify("Invalid JSON-LD — please fix before saving", "error");
//       return;
//     }

//     // shape matches mongoose schema exactly
//     const payload = {
//       pageSlug:  form.pageSlug.trim(),
//       meta: {
//         title:       form.meta.title,
//         description: form.meta.description,
//         keywords:    form.meta.keywords.split(",").map((k) => k.trim()).filter(Boolean),
//         canonical:   form.meta.canonical,
//       },
//       robots: {
//         index:  form.robots.index,
//         follow: form.robots.follow,
//       },
//       openGraph: {
//         title:       form.openGraph.title,
//         description: form.openGraph.description,
//         image:       form.openGraph.image,
//         url:         form.openGraph.url,
//         type:        form.openGraph.type,
//       },
//       twitter: {
//         card:        form.twitter.card,
//         title:       form.twitter.title,
//         description: form.twitter.description,
//         image:       form.twitter.image,
//       },
//       jsonLd: parsedJsonLd,
//       llmTxt: form.llmTxt,
//       sitemap: {
//   include:    form.sitemap.include,
//   changefreq: form.sitemap.changefreq,
//   priority:   parseFloat(form.sitemap.priority),
//   lastmod:    form.sitemap.lastmod || null,
// },
//     };

//     setLoading(true);
//     try {
//       const method = mode === "edit" ? "PUT" : "POST";
//       const url    = mode === "edit" ? `${API}/${form.pageSlug}` : API;
//       const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
//       if (!res.ok) {
//   throw new Error("Failed");
// }
//       notify(mode === "edit" ? "SEO record updated!" : "New SEO record created!");
//       if (mode === "new" && !slugList.includes(form.pageSlug)) {
//         setSlugList((s) => [...s, form.pageSlug]);
//       }
//       setMode("edit");
//     } catch {
//       notify("Failed to save — check your connection", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const jsonLdValid = form.jsonLd.trim()
//     ? (() => { try { JSON.parse(form.jsonLd); return true; } catch { return false; } })()
//     : null;

//   return (
//     <>
//       <div className="seo-wrap">

//         {/* ── Page Header ── */}
//         <div className="seo-page-header">
//           <div className="seo-page-heading">
//             <div className="seo-page-icon">
//               <Search size={20} />
//             </div>
//             <div>
//               <h1>SEO Management</h1>
//               <p>View and manage meta, Open Graph, Twitter &amp; structured data</p>
//             </div>
//           </div>
//           <div className="seo-mode-badge" data-mode={mode}>
//             {mode === "edit" ? <Pencil size={13} /> : <Sparkles size={13} />}
//             {mode === "edit" ? "Editing" : "New Record"}
//           </div>
//         </div>

//         {/* ── Stats Row ── */}
//         <div className="seo-stats">
//           <div className="stat-card blue">
//             <div className="stat-card-icon"><FileStack size={18} /></div>
//             <div className="stat-card-text">
//               <span className="stat-num">{slugList.length}</span>
//               <span className="stat-label">Pages Configured</span>
//             </div>
//           </div>
//           <div className="stat-card green">
//             <div className="stat-card-icon"><ToggleRight size={18} /></div>
//             <div className="stat-card-text">
//               <span className="stat-num">{form.robots.index ? "ON" : "OFF"}</span>
//               <span className="stat-label">Indexing</span>
//             </div>
//           </div>
//           <div className="stat-card yellow">
//             <div className="stat-card-icon"><Link2 size={18} /></div>
//             <div className="stat-card-text">
//               <span className="stat-num">{form.robots.follow ? "ON" : "OFF"}</span>
//               <span className="stat-label">Follow Links</span>
//             </div>
//           </div>
//         </div>

//         <form className="seo-form" onSubmit={handleSubmit}>

//           {/* ── Page Slug ── */}
//           <div className="seo-card slug-card">
//             <div className="slug-card-icon"><Globe size={19} /></div>
//             <div className="slug-card-body">
//               <label className="slug-card-label">Page Slug <span className="required">*</span></label>

//               {/* pick an existing page OR type a new one */}
//               <div className="slug-controls">
//                 <select
//                   className="slug-select"
//                   value={mode === "edit" ? form.pageSlug : ""}
//                   onChange={(e) => loadSlug(e.target.value)}
//                 >
//                   <option value="">— create new page —</option>
//                   {slugList.map((s) => <option key={s} value={s}>{s}</option>)}
//                 </select>

//                 {mode === "new" && (
//                   <div className="slug-row">
//                     <span className="slug-prefix">yoursite.com/</span>
//                     <input
//                       type="text"
//                       value={form.pageSlug}
//                       placeholder="e.g. home, about, products"
//                       onChange={(e) => setForm((f) => ({ ...f, pageSlug: e.target.value.toLowerCase().replace(/\s+/g, "-") }))}
//                     />
//                   </div>
//                 )}

//                 {mode === "edit" && (
//                   <div className="current-slug-display">
//                     <span className="slug-prefix">yoursite.com/</span>
//                     <strong>{form.pageSlug}</strong>
//                     <button type="button" className="new-page-btn" onClick={() => { setForm(DEFAULT_FORM); setMode("new"); }}>
//                       <Plus size={13} />
//                       New Page
//                     </button>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* ── Accordion Sections ── */}
//           {SECTIONS.map(({ key, icon: Icon, accent, label, desc }) => (
//             <div className="seo-card seo-section" key={key}>

//               <button type="button" className="seo-section-header" onClick={() => toggle(key)}>
//                 <div className="section-header-left">
//                   <span className={`section-icon icon-${accent}`}><Icon size={18} /></span>
//                   <div>
//                     <div className="section-label">{label}</div>
//                     <div className="section-desc">{desc}</div>
//                   </div>
//                 </div>
//                 <span className={`section-toggle ${open[key] ? "open" : ""}`}>
//                   <ChevronDown size={15} />
//                 </span>
//               </button>

//               <div className={`seo-section-body ${open[key] ? "" : "hidden"}`}>

//                 {/* ── meta ── */}
//                 {key === "meta" && (
//                   <>
//                     <div className="field-row cols-2">
//                       <div className="field">
//                         <label>Title</label>
//                         <input type="text" placeholder="Page title (50–60 chars)" value={form.meta.title}
//                           onChange={(e) => handleChange("meta", "title", e.target.value)} />
//                         <span className="field-hint">{form.meta.title.length} / 60 chars</span>
//                       </div>
//                       <div className="field">
//                         <label>Canonical URL</label>
//                         <input type="text" placeholder="https://yoursite.com/page" value={form.meta.canonical}
//                           onChange={(e) => handleChange("meta", "canonical", e.target.value)} />
//                       </div>
//                     </div>
//                     <div className="field">
//                       <label>Meta Description</label>
//                       <textarea placeholder="Compelling description (150–160 chars)" value={form.meta.description}
//                         onChange={(e) => handleChange("meta", "description", e.target.value)} />
//                       <span className="field-hint">{form.meta.description.length} / 160 chars</span>
//                     </div>
//                     <div className="field">
//                       <label>Keywords <span className="field-tag">Array — comma separated</span></label>
//                       <input type="text" placeholder="seo, metadata, schema" value={form.meta.keywords}
//                         onChange={(e) => handleChange("meta", "keywords", e.target.value)} />
//                       {form.meta.keywords && (
//                         <div className="keyword-chips">
//                           {form.meta.keywords.split(",").map((k) => k.trim()).filter(Boolean).map((k, i) => (
//                             <span key={i} className="chip">{k}</span>
//                           ))}
//                         </div>
//                       )}
//                     </div>
//                   </>
//                 )}

//                 {/* ── robots ── */}
//                 {key === "robots" && (
//                   <div className="robots-row">
//                     {[
//                       { rkey: "index",  label: "Index",       desc: "Allow search engines to index this page" },
//                       { rkey: "follow", label: "Follow Links", desc: "Allow crawlers to follow links on this page" },
//                     ].map(({ rkey, label: rl, desc: rd }) => (
//                       <label className={`toggle-label ${form.robots[rkey] ? "active" : ""}`} key={rkey}>
//                         <div className="toggle-wrap">
//                           <input type="checkbox" checked={form.robots[rkey]}
//                             onChange={(e) => handleChange("robots", rkey, e.target.checked)} />
//                           <span className="toggle-track" />
//                         </div>
//                         <div className="toggle-text">
//                           <span className="toggle-name">{rl}</span>
//                           <span className="toggle-desc">{rd}</span>
//                         </div>
//                         <span className={`robot-status ${form.robots[rkey] ? "on" : "off"}`}>
//                           {form.robots[rkey] ? "ON" : "OFF"}
//                         </span>
//                       </label>
//                     ))}
//                   </div>
//                 )}

//                 {/* ── openGraph ── */}
//                 {key === "openGraph" && (
//                   <>
//                     <div className="field-row cols-2">
//                       <div className="field">
//                         <label>OG Title</label>
//                         <input type="text" placeholder="Social share title" value={form.openGraph.title}
//                           onChange={(e) => handleChange("openGraph", "title", e.target.value)} />
//                       </div>
//                       <div className="field">
//                         <label>OG Type</label>
//                         <select value={form.openGraph.type} onChange={(e) => handleChange("openGraph", "type", e.target.value)}>
//                           <option value="website">website</option>
//                           <option value="article">article</option>
//                           <option value="product">product</option>
//                           <option value="profile">profile</option>
//                         </select>
//                       </div>
//                     </div>
//                     <div className="field">
//                       <label>OG Description</label>
//                       <textarea placeholder="Description shown when sharing on Facebook / LinkedIn" value={form.openGraph.description}
//                         onChange={(e) => handleChange("openGraph", "description", e.target.value)} />
//                     </div>
//                     <div className="field-row cols-2">
//                       <div className="field">
//                         <label>OG Image URL</label>
//                         <input type="text" placeholder="https://yoursite.com/og-image.jpg" value={form.openGraph.image}
//                           onChange={(e) => handleChange("openGraph", "image", e.target.value)} />
//                       </div>
//                       <div className="field">
//                         <label>OG URL</label>
//                         <input type="text" placeholder="https://yoursite.com" value={form.openGraph.url}
//                           onChange={(e) => handleChange("openGraph", "url", e.target.value)} />
//                       </div>
//                     </div>
//                   </>
//                 )}

//                 {/* ── twitter ── */}
//                 {key === "twitter" && (
//                   <>
//                     <div className="field-row cols-2">
//                       <div className="field">
//                         <label>Twitter Title</label>
//                         <input type="text" placeholder="Card title" value={form.twitter.title}
//                           onChange={(e) => handleChange("twitter", "title", e.target.value)} />
//                       </div>
//                       <div className="field">
//                         <label>Card Type</label>
//                         <select value={form.twitter.card} onChange={(e) => handleChange("twitter", "card", e.target.value)}>
//                           <option value="summary_large_image">summary_large_image</option>
//                           <option value="summary">summary</option>
//                           <option value="app">app</option>
//                           <option value="player">player</option>
//                         </select>
//                       </div>
//                     </div>
//                     <div className="field">
//                       <label>Twitter Description</label>
//                       <textarea placeholder="Card description shown on Twitter" value={form.twitter.description}
//                         onChange={(e) => handleChange("twitter", "description", e.target.value)} />
//                     </div>
//                     <div className="field">
//                       <label>Twitter Image URL</label>
//                       <input type="text" placeholder="https://yoursite.com/twitter-card.jpg" value={form.twitter.image}
//                         onChange={(e) => handleChange("twitter", "image", e.target.value)} />
//                     </div>
//                   </>
//                 )}

//                 {/* ── jsonLd ── */}
//                 {key === "jsonLd" && (
//                   <div className="field">
//                     <label>JSON-LD Schema <span className="field-tag">Mixed type — free JSON</span></label>
//                     <textarea
//                       className="json-editor tall"
//                       placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "WebPage",\n  "name": "Home"\n}'}
//                       value={form.jsonLd}
//                       onChange={(e) => setForm((f) => ({ ...f, jsonLd: e.target.value }))}
//                     />
//                     <span className={`field-hint json-status ${jsonLdValid === false ? "invalid" : jsonLdValid === true ? "valid" : ""}`}>
//                       {jsonLdValid === true && (<><CheckCircle2 size={13} /> Valid JSON</>)}
//                       {jsonLdValid === false && (<><XCircle size={13} /> Invalid JSON</>)}
//                       {jsonLdValid === null && "Paste valid JSON-LD structured data markup"}
//                     </span>
//                   </div>
//                 )}

//                 {key === "llmTxt" && (
//   <div className="field">
//     <label>
//       LLM.txt Content 
//       <span className="field-tag">AI / AEO optimized plain text</span>
//     </label>

//     <textarea
//       className="json-editor tall"
//       placeholder={`# About Our Vehicles

// We manufacture electric vehicles under the following categories:
// - N-First
// - Diplos
// - Institutional

// Our mission is...`}
//       value={form.llmTxt}
//       onChange={(e) =>
//         setForm((f) => ({ ...f, llmTxt: e.target.value }))
//       }
//     />

//     <span className="field-hint">
//       This content will be served as plain text for AI systems.
//     </span>
//   </div>
// )}

// {key === "sitemap" && (
//   <>
//     <div className="robots-row">
//       <label className={`toggle-label ${form.sitemap.include ? "active" : ""}`}>
//         <div className="toggle-wrap">
//           <input type="checkbox" checked={form.sitemap.include}
//             onChange={(e) => handleChange("sitemap", "include", e.target.checked)} />
//           <span className="toggle-track" />
//         </div>
//         <div className="toggle-text">
//           <span className="toggle-name">Include in Sitemap</span>
//           <span className="toggle-desc">Add this page to the XML sitemap</span>
//         </div>
//         <span className={`robot-status ${form.sitemap.include ? "on" : "off"}`}>
//           {form.sitemap.include ? "ON" : "OFF"}
//         </span>
//       </label>
//     </div>

//     <div className="field-row cols-2">
//       <div className="field">
//         <label>Change Frequency</label>
//         <select value={form.sitemap.changefreq}
//           onChange={(e) => handleChange("sitemap", "changefreq", e.target.value)}>
//           <option value="always">always</option>
//           <option value="hourly">hourly</option>
//           <option value="daily">daily</option>
//           <option value="weekly">weekly</option>
//           <option value="monthly">monthly</option>
//           <option value="yearly">yearly</option>
//           <option value="never">never</option>
//         </select>
//         <span className="field-hint">How often the page content changes</span>
//       </div>
//       <div className="field">
//         <label>Priority <span className="field-tag">0.0 – 1.0</span></label>
//         <select value={form.sitemap.priority}
//           onChange={(e) => handleChange("sitemap", "priority", e.target.value)}>
//           {["1.0","0.9","0.8","0.7","0.6","0.5","0.4","0.3","0.2","0.1","0.0"].map(p => (
//             <option key={p} value={p}>{p}</option>
//           ))}
//         </select>
//         <span className="field-hint">Relative importance vs other pages</span>
//       </div>
//     </div>

//     <div className="field">
//       <label>Last Modified Date <span className="field-tag">Optional</span></label>
//       <input type="date" value={form.sitemap.lastmod}
//         onChange={(e) => handleChange("sitemap", "lastmod", e.target.value)} />
//       <span className="field-hint">Leave blank to use today's date automatically</span>
//     </div>
//   </>
// )}

//               </div>
//             </div>
//           ))}

//           {/* ── Submit ── */}
//           <div className="submit-row">
//             <span className="save-hint">
//               {mode === "edit" ? `Updating: /${form.pageSlug}` : "Creating new page SEO record"}
//             </span>
//             <button type="submit" className="save-btn" disabled={loading}>
//               {loading ? "Saving..." : (
//                 <>
//                   <Save size={15} />
//                   {mode === "edit" ? "Update SEO" : "Save SEO"}
//                 </>
//               )}
//             </button>
//           </div>

//         </form>
//       </div>

//       {/* ── Toast ── */}
//       {notif && (
//         <div className={`notif ${notif.type === "error" ? "error" : ""}`}>
//           <span className="notif-icon">{notif.type === "error" ? <X size={14} /> : <Check size={14} />}</span>
//           {notif.msg}
//         </div>
//       )}
//     </>
//   );
// }



// import React, { useState, useEffect } from "react";
// import "../css/Seoadmin.css";

// const API = "/api/seo";

// const DEFAULT_FORM = {
//   pageSlug: "",
//   meta:      { title: "", description: "", keywords: "", canonical: "" },
//   robots:    { index: true, follow: true },
//   openGraph: { title: "", description: "", image: "", url: "", type: "website" },
//   twitter:   { card: "summary_large_image", title: "", description: "", image: "" },
//   jsonLd:    "",
//   llmTxt: "", 
//   sitemap: { include: true, changefreq: "weekly", priority: "0.8", lastmod: "" }
// };

// const SECTIONS = [
//   { key: "meta",      icon: "🏷️",  label: "Meta Tags",       desc: "Title, description & keywords" },
//   { key: "robots",    icon: "🤖",  label: "Robots Settings", desc: "Indexing & crawl directives"   },
//   { key: "openGraph", icon: "🔗",  label: "Open Graph",      desc: "Facebook & social sharing"     },
//   { key: "twitter",   icon: "🐦",  label: "Twitter Card",    desc: "Twitter preview data"          },
//   { key: "jsonLd",    icon: "📋",  label: "Structured Data", desc: "JSON-LD schema markup"         },
//   { key: "llmTxt",    icon: "🧠",  label: "LLM.txt (AEO)",   desc: "AI-readable structured content" },
//   { key: "sitemap",   icon: "🗺️", label: "Sitemap", desc: "XML sitemap configuration" },
// ];

// export default function SEOSchema() {
//   const [form, setForm]         = useState(DEFAULT_FORM);
//   const [slugList, setSlugList] = useState([]);          // existing page slugs from DB
//   const [mode, setMode]         = useState("new");       // "new" | "edit"
//   const [open, setOpen]         = useState({ meta: true, robots: false, openGraph: false, twitter: false, jsonLd: false, llmTxt: false, sitemap: false });
//   const [notif, setNotif]       = useState(null);
//   const [loading, setLoading]   = useState(false);

//   // ── fetch all existing slugs on mount ──
//   useEffect(() => {
//     fetch(API)
//       .then((r) => r.json())
//       .then((data) => {
//         // expects either an array of docs or { data: [] }
//         const docs = Array.isArray(data) ? data : data.data ?? [];
//         setSlugList(docs.map((d) => d.pageSlug));
//       })
//       .catch(() => {}); // silently fail if API not running
//   }, []);

//   // ── load existing record when a slug is chosen ──
//   const loadSlug = async (slug) => {
//     if (!slug) { setForm(DEFAULT_FORM); setMode("new"); return; }
//     setLoading(true);
//     try {
//       const res  = await fetch(`${API}/${slug}`);
//       const data = await res.json();
//       const doc  = data.data ?? data;
//       setForm({
//         pageSlug:  doc.pageSlug ?? slug,
//         meta:      { title: doc.meta?.title ?? "", description: doc.meta?.description ?? "", keywords: Array.isArray(doc.meta?.keywords) ? doc.meta.keywords.join(", ") : (doc.meta?.keywords ?? ""), canonical: doc.meta?.canonical ?? "" },
//         robots:    { index: doc.robots?.index ?? true, follow: doc.robots?.follow ?? true },
//         openGraph: { title: doc.openGraph?.title ?? "", description: doc.openGraph?.description ?? "", image: doc.openGraph?.image ?? "", url: doc.openGraph?.url ?? "", type: doc.openGraph?.type ?? "website" },
//         twitter:   { card: doc.twitter?.card ?? "summary_large_image", title: doc.twitter?.title ?? "", description: doc.twitter?.description ?? "", image: doc.twitter?.image ?? "" },
//         jsonLd:    doc.jsonLd ? JSON.stringify(doc.jsonLd, null, 2) : "",
//         llmTxt:    doc.llmTxt ?? "",
//        sitemap: {
//   include: doc.sitemap?.include ?? true,
//   changefreq: doc.sitemap?.changefreq ?? "weekly",
//   priority: String(doc.sitemap?.priority ?? "0.8"),
//   lastmod: doc.sitemap?.lastmod ?? "",
// },
//       });
//       setMode("edit");
//     } catch {
//       notify("Failed to load page data", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const notify = (msg, type = "success") => {
//     setNotif({ msg, type });
//     setTimeout(() => setNotif(null), 3500);
//   };

//   const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

//   const handleChange = (section, field, value) => {
//     setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!form.pageSlug.trim()) { notify("Page slug is required", "error"); return; }

//     let parsedJsonLd = {};
//     try {
//       parsedJsonLd = form.jsonLd.trim() ? JSON.parse(form.jsonLd) : {};
//     } catch {
//       notify("Invalid JSON-LD — please fix before saving", "error");
//       return;
//     }

//     // shape matches mongoose schema exactly
//     const payload = {
//       pageSlug:  form.pageSlug.trim(),
//       meta: {
//         title:       form.meta.title,
//         description: form.meta.description,
//         keywords:    form.meta.keywords.split(",").map((k) => k.trim()).filter(Boolean),
//         canonical:   form.meta.canonical,
//       },
//       robots: {
//         index:  form.robots.index,
//         follow: form.robots.follow,
//       },
//       openGraph: {
//         title:       form.openGraph.title,
//         description: form.openGraph.description,
//         image:       form.openGraph.image,
//         url:         form.openGraph.url,
//         type:        form.openGraph.type,
//       },
//       twitter: {
//         card:        form.twitter.card,
//         title:       form.twitter.title,
//         description: form.twitter.description,
//         image:       form.twitter.image,
//       },
//       jsonLd: parsedJsonLd,
//       llmTxt: form.llmTxt,
//       sitemap: {
//   include:    form.sitemap.include,
//   changefreq: form.sitemap.changefreq,
//   priority:   parseFloat(form.sitemap.priority),
//   lastmod:    form.sitemap.lastmod || null,
// },
//     };

//     setLoading(true);
//     try {
//       const method = mode === "edit" ? "PUT" : "POST";
//       const url    = mode === "edit" ? `${API}/${form.pageSlug}` : API;
//       const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
//       if (!res.ok) {
//   throw new Error("Failed");
// }
//       notify(mode === "edit" ? "SEO record updated!" : "New SEO record created!");
//       if (mode === "new" && !slugList.includes(form.pageSlug)) {
//         setSlugList((s) => [...s, form.pageSlug]);
//       }
//       setMode("edit");
//     } catch {
//       notify("Failed to save — check your connection", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <>
//       <div className="seo-wrap">

      
//         <div className="seo-page-header">
//           <div>
//             <h1>SEO Management</h1>
//             <p>View and manage meta, Open Graph, Twitter & structured data</p>
//           </div>
//           <div className="seo-mode-badge" data-mode={mode}>
//             {mode === "edit" ? "✏️ Editing" : "✨ New Record"}
//           </div>
//         </div>

       
//         <div className="seo-stats">
//           <div className="stat-card blue">
//             <span className="stat-num">{slugList.length}</span>
//             <span className="stat-label">Pages Configured</span>
//           </div>
//           <div className="stat-card green">
//             <span className="stat-num">{form.robots.index ? "ON" : "OFF"}</span>
//             <span className="stat-label">Indexing</span>
//           </div>
//           <div className="stat-card yellow">
//             <span className="stat-num">{form.robots.follow ? "ON" : "OFF"}</span>
//             <span className="stat-label">Follow Links</span>
//           </div>
//         </div>

//         <form className="seo-form" onSubmit={handleSubmit}>

        
//           <div className="seo-card slug-card">
//             <div className="slug-card-icon">🌐</div>
//             <div className="slug-card-body">
//               <label className="slug-card-label">Page Slug <span className="required">*</span></label>

             
//               <div className="slug-controls">
//                 <select
//                   className="slug-select"
//                   value={mode === "edit" ? form.pageSlug : ""}
//                   onChange={(e) => loadSlug(e.target.value)}
//                 >
//                   <option value="">— create new page —</option>
//                   {slugList.map((s) => <option key={s} value={s}>{s}</option>)}
//                 </select>

//                 {mode === "new" && (
//                   <div className="slug-row">
//                     <span className="slug-prefix">yoursite.com/</span>
//                     <input
//                       type="text"
//                       value={form.pageSlug}
//                       placeholder="e.g. home, about, products"
//                       onChange={(e) => setForm((f) => ({ ...f, pageSlug: e.target.value.toLowerCase().replace(/\s+/g, "-") }))}
//                     />
//                   </div>
//                 )}

//                 {mode === "edit" && (
//                   <div className="current-slug-display">
//                     <span className="slug-prefix">yoursite.com/</span>
//                     <strong>{form.pageSlug}</strong>
//                     <button type="button" className="new-page-btn" onClick={() => { setForm(DEFAULT_FORM); setMode("new"); }}>
//                       + New Page
//                     </button>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

     
//           {SECTIONS.map(({ key, icon, label, desc }) => (
//             <div className="seo-card seo-section" key={key}>

//               <button type="button" className="seo-section-header" onClick={() => toggle(key)}>
//                 <div className="section-header-left">
//                   <span className="section-icon">{icon}</span>
//                   <div>
//                     <div className="section-label">{label}</div>
//                     <div className="section-desc">{desc}</div>
//                   </div>
//                 </div>
//                 <span className={`section-toggle ${open[key] ? "open" : ""}`}>▾</span>
//               </button>

//               <div className={`seo-section-body ${open[key] ? "" : "hidden"}`}>

//                 {key === "meta" && (
//                   <>
//                     <div className="field-row cols-2">
//                       <div className="field">
//                         <label>Title</label>
//                         <input type="text" placeholder="Page title (50–60 chars)" value={form.meta.title}
//                           onChange={(e) => handleChange("meta", "title", e.target.value)} />
//                         <span className="field-hint">{form.meta.title.length} / 60 chars</span>
//                       </div>
//                       <div className="field">
//                         <label>Canonical URL</label>
//                         <input type="text" placeholder="https://yoursite.com/page" value={form.meta.canonical}
//                           onChange={(e) => handleChange("meta", "canonical", e.target.value)} />
//                       </div>
//                     </div>
//                     <div className="field">
//                       <label>Meta Description</label>
//                       <textarea placeholder="Compelling description (150–160 chars)" value={form.meta.description}
//                         onChange={(e) => handleChange("meta", "description", e.target.value)} />
//                       <span className="field-hint">{form.meta.description.length} / 160 chars</span>
//                     </div>
//                     <div className="field">
//                       <label>Keywords <span className="field-tag">Array — comma separated</span></label>
//                       <input type="text" placeholder="seo, metadata, schema" value={form.meta.keywords}
//                         onChange={(e) => handleChange("meta", "keywords", e.target.value)} />
//                       {form.meta.keywords && (
//                         <div className="keyword-chips">
//                           {form.meta.keywords.split(",").map((k) => k.trim()).filter(Boolean).map((k, i) => (
//                             <span key={i} className="chip">{k}</span>
//                           ))}
//                         </div>
//                       )}
//                     </div>
//                   </>
//                 )}

//                 {key === "robots" && (
//                   <div className="robots-row">
//                     {[
//                       { rkey: "index",  label: "Index",       desc: "Allow search engines to index this page" },
//                       { rkey: "follow", label: "Follow Links", desc: "Allow crawlers to follow links on this page" },
//                     ].map(({ rkey, label: rl, desc: rd }) => (
//                       <label className={`toggle-label ${form.robots[rkey] ? "active" : ""}`} key={rkey}>
//                         <div className="toggle-wrap">
//                           <input type="checkbox" checked={form.robots[rkey]}
//                             onChange={(e) => handleChange("robots", rkey, e.target.checked)} />
//                           <span className="toggle-track" />
//                         </div>
//                         <div className="toggle-text">
//                           <span className="toggle-name">{rl}</span>
//                           <span className="toggle-desc">{rd}</span>
//                         </div>
//                         <span className={`robot-status ${form.robots[rkey] ? "on" : "off"}`}>
//                           {form.robots[rkey] ? "ON" : "OFF"}
//                         </span>
//                       </label>
//                     ))}
//                   </div>
//                 )}

//                 {key === "openGraph" && (
//                   <>
//                     <div className="field-row cols-2">
//                       <div className="field">
//                         <label>OG Title</label>
//                         <input type="text" placeholder="Social share title" value={form.openGraph.title}
//                           onChange={(e) => handleChange("openGraph", "title", e.target.value)} />
//                       </div>
//                       <div className="field">
//                         <label>OG Type</label>
//                         <select value={form.openGraph.type} onChange={(e) => handleChange("openGraph", "type", e.target.value)}>
//                           <option value="website">website</option>
//                           <option value="article">article</option>
//                           <option value="product">product</option>
//                           <option value="profile">profile</option>
//                         </select>
//                       </div>
//                     </div>
//                     <div className="field">
//                       <label>OG Description</label>
//                       <textarea placeholder="Description shown when sharing on Facebook / LinkedIn" value={form.openGraph.description}
//                         onChange={(e) => handleChange("openGraph", "description", e.target.value)} />
//                     </div>
//                     <div className="field-row cols-2">
//                       <div className="field">
//                         <label>OG Image URL</label>
//                         <input type="text" placeholder="https://yoursite.com/og-image.jpg" value={form.openGraph.image}
//                           onChange={(e) => handleChange("openGraph", "image", e.target.value)} />
//                       </div>
//                       <div className="field">
//                         <label>OG URL</label>
//                         <input type="text" placeholder="https://yoursite.com" value={form.openGraph.url}
//                           onChange={(e) => handleChange("openGraph", "url", e.target.value)} />
//                       </div>
//                     </div>
//                   </>
//                 )}

//                 {key === "twitter" && (
//                   <>
//                     <div className="field-row cols-2">
//                       <div className="field">
//                         <label>Twitter Title</label>
//                         <input type="text" placeholder="Card title" value={form.twitter.title}
//                           onChange={(e) => handleChange("twitter", "title", e.target.value)} />
//                       </div>
//                       <div className="field">
//                         <label>Card Type</label>
//                         <select value={form.twitter.card} onChange={(e) => handleChange("twitter", "card", e.target.value)}>
//                           <option value="summary_large_image">summary_large_image</option>
//                           <option value="summary">summary</option>
//                           <option value="app">app</option>
//                           <option value="player">player</option>
//                         </select>
//                       </div>
//                     </div>
//                     <div className="field">
//                       <label>Twitter Description</label>
//                       <textarea placeholder="Card description shown on Twitter" value={form.twitter.description}
//                         onChange={(e) => handleChange("twitter", "description", e.target.value)} />
//                     </div>
//                     <div className="field">
//                       <label>Twitter Image URL</label>
//                       <input type="text" placeholder="https://yoursite.com/twitter-card.jpg" value={form.twitter.image}
//                         onChange={(e) => handleChange("twitter", "image", e.target.value)} />
//                     </div>
//                   </>
//                 )}

//                 {key === "jsonLd" && (
//                   <div className="field">
//                     <label>JSON-LD Schema <span className="field-tag">Mixed type — free JSON</span></label>
//                     <textarea
//                       className="json-editor tall"
//                       placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "WebPage",\n  "name": "Home"\n}'}
//                       value={form.jsonLd}
//                       onChange={(e) => setForm((f) => ({ ...f, jsonLd: e.target.value }))}
//                     />
//                     <span className="field-hint">
//                       {form.jsonLd.trim() ? ((() => { try { JSON.parse(form.jsonLd); return "✅ Valid JSON"; } catch { return "❌ Invalid JSON"; } })()) : "Paste valid JSON-LD structured data markup"}
//                     </span>
//                   </div>
//                 )}

//                 {key === "llmTxt" && (
//   <div className="field">
//     <label>
//       LLM.txt Content 
//       <span className="field-tag">AI / AEO optimized plain text</span>
//     </label>

//     <textarea
//       className="json-editor tall"
//       placeholder={`# About Our Vehicles

// We manufacture electric vehicles under the following categories:
// - N-First
// - Diplos
// - Institutional

// Our mission is...`}
//       value={form.llmTxt}
//       onChange={(e) =>
//         setForm((f) => ({ ...f, llmTxt: e.target.value }))
//       }
//     />

//     <span className="field-hint">
//       This content will be served as plain text for AI systems.
//     </span>
//   </div>
// )}

// {key === "sitemap" && (
//   <>
//     <div className="robots-row">
//       <label className={`toggle-label ${form.sitemap.include ? "active" : ""}`}>
//         <div className="toggle-wrap">
//           <input type="checkbox" checked={form.sitemap.include}
//             onChange={(e) => handleChange("sitemap", "include", e.target.checked)} />
//           <span className="toggle-track" />
//         </div>
//         <div className="toggle-text">
//           <span className="toggle-name">Include in Sitemap</span>
//           <span className="toggle-desc">Add this page to the XML sitemap</span>
//         </div>
//         <span className={`robot-status ${form.sitemap.include ? "on" : "off"}`}>
//           {form.sitemap.include ? "ON" : "OFF"}
//         </span>
//       </label>
//     </div>

//     <div className="field-row cols-2">
//       <div className="field">
//         <label>Change Frequency</label>
//         <select value={form.sitemap.changefreq}
//           onChange={(e) => handleChange("sitemap", "changefreq", e.target.value)}>
//           <option value="always">always</option>
//           <option value="hourly">hourly</option>
//           <option value="daily">daily</option>
//           <option value="weekly">weekly</option>
//           <option value="monthly">monthly</option>
//           <option value="yearly">yearly</option>
//           <option value="never">never</option>
//         </select>
//         <span className="field-hint">How often the page content changes</span>
//       </div>
//       <div className="field">
//         <label>Priority <span className="field-tag">0.0 – 1.0</span></label>
//         <select value={form.sitemap.priority}
//           onChange={(e) => handleChange("sitemap", "priority", e.target.value)}>
//           {["1.0","0.9","0.8","0.7","0.6","0.5","0.4","0.3","0.2","0.1","0.0"].map(p => (
//             <option key={p} value={p}>{p}</option>
//           ))}
//         </select>
//         <span className="field-hint">Relative importance vs other pages</span>
//       </div>
//     </div>

//     <div className="field">
//       <label>Last Modified Date <span className="field-tag">Optional</span></label>
//       <input type="date" value={form.sitemap.lastmod}
//         onChange={(e) => handleChange("sitemap", "lastmod", e.target.value)} />
//       <span className="field-hint">Leave blank to use today's date automatically</span>
//     </div>
//   </>
// )}

//               </div>
//             </div>
//           ))}

//           <div className="submit-row">
//             <span className="save-hint">
//               {mode === "edit" ? `Updating: /${form.pageSlug}` : "Creating new page SEO record"}
//             </span>
//             <button type="submit" className="save-btn" disabled={loading}>
//               {loading ? "Saving..." : mode === "edit" ? "Update SEO" : "Save SEO"}
//             </button>
//           </div>

//         </form>
//       </div>

//       {notif && (
//         <div className={`notif ${notif.type === "error" ? "error" : ""}`}>
//           <span className="notif-icon">{notif.type === "error" ? "✕" : "✓"}</span>
//           {notif.msg}
//         </div>
//       )}
//     </>
//   );
// }